import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatCents } from '@affily/shared/money';
import { cardFeeCents } from '@affily/shared/commission';
import { BANK_TRANSFER_CURRENCIES } from '@affily/shared/constants';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { BillingSetup } from '../../components/BillingSetup.jsx';
import {
  Button,
  Badge,
  Spinner,
  EmptyState,
  Alert,
  SectionTitle,
} from '../../components/ui.jsx';
import { HelpLink } from '../Help.jsx';

/**
 * Settle and pay.
 *
 * Two deliberate steps. Settling is the brand confirming the orders are real
 * and not refunded; paying moves the money. Collapsing them into one button
 * would mean paying out on orders that hadn't cleared a refund window yet.
 */
export default function BrandSettle() {
  const approved = useFetch('/dashboard/brand/commissions?status=approved');
  const settled = useFetch('/dashboard/brand/commissions?status=settled');
  const stripe = useFetch('/stripe/status');
  // The card fee and the bank-transfer option come from the server's settings.
  const billing = useFetch('/stripe/billing');

  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const approvedRows = approved.data?.commissions ?? [];
  const settledRows = settled.data?.commissions ?? [];

  /**
   * Settled money grouped per creator and currency — a payout is per creator,
   * not per line, and a euro commission is never paid out together with a
   * dollar one.
   */
  const byCreator = useMemo(() => {
    const map = new Map();
    for (const c of settledRows) {
      if (!c.creator) continue;
      const currency = c.currency ?? 'usd';
      const key = `${c.creator.id}:${currency}`;
      const entry = map.get(key) ?? {
        key,
        creator: c.creator,
        currency,
        count: 0,
        grossCents: 0,
        brandFeeCents: 0,
        netCents: 0,
      };
      entry.count += 1;
      entry.grossCents += c.grossAmountCents;
      entry.brandFeeCents += c.brandFeeCents ?? 0;
      entry.netCents += c.netAmountCents;
      map.set(key, entry);
    }
    return [...map.values()];
  }, [settledRows]);

  // One figure per currency: "€650.00 · $40.00", never one mixed sum.
  const selectedTotals = new Map();
  for (const c of approvedRows.filter((row) => selected.has(row.id))) {
    const currency = c.currency ?? 'usd';
    selectedTotals.set(currency, (selectedTotals.get(currency) ?? 0) + c.grossAmountCents + (c.brandFeeCents ?? 0));
  }
  const selectedTotal = [...selectedTotals]
    .map(([currency, cents]) => formatCents(cents, { currency }))
    .join(' · ');

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function settle(ids) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const { settledCount } = await api.post('/stripe/settle', { commissionIds: ids });
      setNotice(`Settled ${settledCount} commission${settledCount === 1 ? '' : 's'}.`);
      setSelected(new Set());
      approved.reload();
      settled.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const cardFee = billing.data?.cardFee;
  const bank = billing.data?.bankTransfer;
  const bankBalance = (currency) => bank?.balances?.find((b) => b.currency === currency)?.availableCents ?? 0;
  const feeOnCard = (cents) => (cardFee ? cardFeeCents(cents, cardFee) : 0);

  async function payout(creatorId, currency, method = 'card') {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const { payout: p } = await api.post('/stripe/payouts', { creatorId, currency, method });
      setNotice(
        `Paid the creator ${formatCents(p.netCents, { currency: p.currency })} across ${p.commissionCount} commissions` +
          ` — ${formatCents(p.chargedCents, { currency: p.currency })} ${p.method === 'bank_transfer' ? 'from your bank-transfer balance' : 'charged to your card'}.`,
      );
      settled.reload();
      billing.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DashboardLayout
      side="brand"
      title="Settle & pay"
      description={
        <>
          Confirm the commissions you owe, then release them.{' '}
          <HelpLink slug="how-do-i-pay-creators">How paying works</HelpLink>
        </>
      }
    >
      <div className="space-y-8">
        <Alert>{error}</Alert>

        {/* Nothing here can complete without a payment method, so it leads. */}
        <BillingSetup compact />
        {notice && <Alert tone="positive">{notice}</Alert>}

        {stripe.data && !stripe.data.enabled && (
          <Alert tone="caution">
            Stripe isn’t configured on this server, so payouts can’t run yet. You can still settle
            commissions — nothing is lost, and they’ll pay out once a key is set.
          </Alert>
        )}

        {/* ── Step 1 ─────────────────────────────────────────────────── */}
        <section>
          <SectionTitle
            hint="Confirm these orders are real and not refunded"
            action={
              approvedRows.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => settle(approvedRows.map((c) => c.id))}
                    disabled={busy}
                  >
                    Settle all
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => settle([...selected])}
                    disabled={busy || selected.size === 0}
                  >
                    Settle {selected.size > 0 ? `${selected.size} · ${selectedTotal}` : 'selected'}
                  </Button>
                </div>
              )
            }
          >
            1. Approved, awaiting settlement
          </SectionTitle>

          {approved.loading ? (
            <Spinner />
          ) : approvedRows.length === 0 ? (
            <EmptyState title="Nothing awaiting settlement" />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={selected.size === approvedRows.length && approvedRows.length > 0}
                        onChange={(e) =>
                          setSelected(
                            e.target.checked ? new Set(approvedRows.map((c) => c.id)) : new Set(),
                          )
                        }
                        className="size-4 accent-accent"
                      />
                    </th>
                    <th>Date</th>
                    <th>Creator</th>
                    <th>Program</th>
                    <th className="num">Order</th>
                    <th className="num">Commission</th>
                    <th className="num">You pay</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedRows.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select commission from ${c.occurredAt}`}
                          checked={selected.has(c.id)}
                          onChange={() => toggle(c.id)}
                          className="size-4 accent-accent"
                        />
                      </td>
                      <td className="whitespace-nowrap text-ink-muted">
                        {new Date(c.occurredAt).toLocaleDateString()}
                      </td>
                      <td>
                        {c.creator ? (
                          <Link
                            to={`/creators/${c.creator.slug}`}
                            className="font-medium hover:text-accent"
                          >
                            {c.creator.displayName}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="text-sm text-ink-muted">
                        {c.program?.title ?? '—'}
                        {c.prize && <span className="block text-xs text-accent">Prize: {c.prize}</span>}
                        {c.post && (
                          <a href={c.post} target="_blank" rel="noopener noreferrer nofollow" className="block max-w-56 truncate text-xs text-accent hover:underline">
                            Post approved: {c.post}
                          </a>
                        )}
                      </td>
                      <td className="num text-ink-muted">
                        {c.orderAmountCents ? formatCents(c.orderAmountCents, { currency: c.currency }) : '—'}
                      </td>
                      <td className="num text-ink-muted">{formatCents(c.grossAmountCents, { currency: c.currency })}</td>
                      <td className="num font-medium">
                        {formatCents(c.grossAmountCents + (c.brandFeeCents ?? 0), { currency: c.currency })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Step 2 ─────────────────────────────────────────────────── */}
        <section>
          <SectionTitle hint="Grouped per creator — one transfer each">
            2. Settled, ready to pay
          </SectionTitle>

          {settled.loading ? (
            <Spinner />
          ) : byCreator.length === 0 ? (
            <EmptyState title="Nothing ready to pay" />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Creator</th>
                    <th className="num">Commissions</th>
                    <th className="num">Commission</th>
                    <th className="num">Affily fee</th>
                    <th className="num">You pay</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {byCreator.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <Link
                          to={`/creators/${row.creator.slug}`}
                          className="font-medium hover:text-accent"
                        >
                          {row.creator.displayName}
                        </Link>
                        {!row.creator.payoutsEnabled && (
                          <Badge tone="caution" className="ml-2">
                            Stripe not ready
                          </Badge>
                        )}
                      </td>
                      <td className="num">{row.count}</td>
                      <td className="num">{formatCents(row.grossCents, { currency: row.currency })}</td>
                      <td className="num text-ink-muted">+{formatCents(row.brandFeeCents, { currency: row.currency })}</td>
                      <td className="num font-medium">
                        {formatCents(row.grossCents + row.brandFeeCents, { currency: row.currency })}
                      </td>
                      <td className="num">
                        <div className="flex flex-col items-end gap-1">
                          <Button
                            size="sm"
                            onClick={() => payout(row.creator.id, row.currency)}
                            disabled={busy || !row.creator.payoutsEnabled || !stripe.data?.enabled}
                            title={
                              !row.creator.payoutsEnabled
                                ? 'This creator hasn’t finished Stripe onboarding, so Stripe would refuse the transfer'
                                : undefined
                            }
                          >
                            Pay by card ·{' '}
                            {formatCents(row.grossCents + row.brandFeeCents + feeOnCard(row.grossCents + row.brandFeeCents), {
                              currency: row.currency,
                            })}
                          </Button>
                          {BANK_TRANSFER_CURRENCIES.includes(row.currency) && bankBalance(row.currency) > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => payout(row.creator.id, row.currency, 'bank_transfer')}
                              disabled={
                                busy ||
                                !row.creator.payoutsEnabled ||
                                bankBalance(row.currency) < row.grossCents + row.brandFeeCents
                              }
                              title={
                                bankBalance(row.currency) < row.grossCents + row.brandFeeCents
                                  ? 'Your bank-transfer balance doesn’t cover this yet'
                                  : undefined
                              }
                            >
                              Pay from bank balance · {formatCents(row.grossCents + row.brandFeeCents, { currency: row.currency })}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-2 text-xs text-ink-faint">
            You pay each commission plus your share of Affily’s fee
            {cardFee &&
              `; a card payment adds ${cardFee.bps / 100}% plus ${(cardFee.fixedCents / 100).toFixed(2)} in the payment’s currency`}
            . <Link to="/dashboard/pricing" className="text-accent hover:underline">Pricing</Link>
          </p>
        </section>

        {bank?.eligible && <BankTransfer bank={bank} onBalance={billing.reload} />}
      </div>
    </DashboardLayout>
  );
}

/**
 * Paying a large amount by bank transfer: the brand's own IBAN at Stripe. What
 * arrives sits as the brand's balance, and "Pay from bank balance" above draws
 * on it with no card fee.
 */
function BankTransfer({ bank, onBalance }) {
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const currency = bank.currencies?.[0] ?? 'eur';
  const balance = bank.balances?.find((b) => b.currency === currency)?.availableCents ?? 0;

  async function show() {
    setBusy(true);
    setError('');
    try {
      setDetails(await api.post('/stripe/bank-transfer', { currency }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function copy(label, value) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      window.prompt(`Copy the ${label}`, value);
    }
  }

  return (
    <section>
      <SectionTitle hint={`For ${formatCents(bank.minCents, { currency, dropZeroCents: true })} or more — no card fee`}>
        Pay by bank transfer
      </SectionTitle>
      <div className="space-y-3 rounded-lg border border-rule bg-paper-raised p-4 text-sm">
        <p className="text-ink-muted">
          Send the money to your own account at Stripe, Affily’s payment provider. When it arrives it shows as your
          balance here, and paying a creator from it adds no card fee.
        </p>
        <p>
          Balance: <strong className="tabular-nums">{formatCents(balance, { currency })}</strong>{' '}
          <button type="button" onClick={onBalance} className="text-xs text-accent hover:underline">
            refresh
          </button>
        </p>
        <Alert>{error}</Alert>
        {details ? (
          <dl className="grid gap-2 sm:grid-cols-[10rem_1fr]">
            {[
              ['IBAN', details.account.iban],
              ['BIC', details.account.bic],
              ['Account holder', details.account.accountHolderName],
              ['Amount you owe', formatCents(details.owedCents, { currency })],
            ].map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-ink-muted">{label}</dt>
                <dd className="flex items-center gap-2 font-mono text-xs">
                  {value}
                  {label !== 'Amount you owe' && value && (
                    <button type="button" onClick={() => copy(label, value)} className="font-sans text-accent hover:underline">
                      {copied === label ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <Button size="sm" variant="outline" onClick={show} disabled={busy}>
            {busy ? 'Getting your details…' : 'Show bank details'}
          </Button>
        )}
        <p className="text-xs text-ink-faint">
          Bank transfers usually take one to two working days to arrive. Your own bank may charge for sending one.
        </p>
      </div>
    </section>
  );
}
